import { useEffect, useState } from 'react';
import { dashboardService, type UserGrowthMonthData } from '../services/dashboardService';
import type { FeedbackData } from '../../../types/feedback';
import type { ObsessLessUser } from '../../../types/commonTypes';
import type { DeletionHealthCounts } from '../../AccountDeletions/types/accountDeletionTypes';
import type { ReferralCodeData } from '../../ReferralCodes/services/referralCodesService';
import type { UserTriggerLogStats } from '../../Stats/services/statsService';

interface WidgetState<T> {
    data: T;
    loading: boolean;
    error: string | null;
}

export interface DashboardData {
    latestFeedbacks: WidgetState<FeedbackData[]>;
    topQuestUsers: WidgetState<ObsessLessUser[]>;
    topTriggerUsers: WidgetState<UserTriggerLogStats[]>;
    userGrowth: WidgetState<UserGrowthMonthData[]>;
    latestReferralCodes: WidgetState<ReferralCodeData[]>;
    accountDeletions: WidgetState<DeletionHealthCounts>;
}

const EMPTY_DELETION_COUNTS: DeletionHealthCounts = {
    inProgress: 0,
    completed: 0,
    failed: 0,
    stalled: 0,
};

const loadingState = <T>(emptyData: T): WidgetState<T> => ({
    data: emptyData,
    loading: true,
    error: null,
});

export const useDashboardData = (): DashboardData => {
    const [latestFeedbacks, setLatestFeedbacks] = useState<WidgetState<FeedbackData[]>>(
        loadingState([])
    );
    const [topQuestUsers, setTopQuestUsers] = useState<WidgetState<ObsessLessUser[]>>(
        loadingState([])
    );
    const [topTriggerUsers, setTopTriggerUsers] = useState<WidgetState<UserTriggerLogStats[]>>(
        loadingState([])
    );
    const [userGrowth, setUserGrowth] = useState<WidgetState<UserGrowthMonthData[]>>(
        loadingState([])
    );
    const [latestReferralCodes, setLatestReferralCodes] = useState<WidgetState<ReferralCodeData[]>>(
        loadingState([])
    );
    const [accountDeletions, setAccountDeletions] = useState<WidgetState<DeletionHealthCounts>>(
        loadingState(EMPTY_DELETION_COUNTS)
    );

    useEffect(() => {
        const settle = async () => {
            const [feedbacks, questUsers, triggerUsers, growth, referralCodes, deletions] =
                await Promise.allSettled([
                    dashboardService.getLatestFeedbacks(3),
                    dashboardService.getTopQuestJourneyUsers(3),
                    dashboardService.getTopTriggerLogUsers(3),
                    dashboardService.getUserGrowthLast3Months(),
                    dashboardService.getLatestUpdatedReferralCodes(3),
                    dashboardService.getAccountDeletionHealth(),
                ]);

            setLatestFeedbacks({
                data: feedbacks.status === 'fulfilled' ? feedbacks.value : [],
                loading: false,
                error: feedbacks.status === 'rejected' ? String(feedbacks.reason) : null,
            });

            setTopQuestUsers({
                data: questUsers.status === 'fulfilled' ? questUsers.value : [],
                loading: false,
                error: questUsers.status === 'rejected' ? String(questUsers.reason) : null,
            });

            setTopTriggerUsers({
                data: triggerUsers.status === 'fulfilled' ? triggerUsers.value : [],
                loading: false,
                error: triggerUsers.status === 'rejected' ? String(triggerUsers.reason) : null,
            });

            setUserGrowth({
                data: growth.status === 'fulfilled' ? growth.value : [],
                loading: false,
                error: growth.status === 'rejected' ? String(growth.reason) : null,
            });

            setLatestReferralCodes({
                data: referralCodes.status === 'fulfilled' ? referralCodes.value : [],
                loading: false,
                error: referralCodes.status === 'rejected' ? String(referralCodes.reason) : null,
            });

            setAccountDeletions({
                data: deletions.status === 'fulfilled' ? deletions.value : EMPTY_DELETION_COUNTS,
                loading: false,
                error: deletions.status === 'rejected' ? String(deletions.reason) : null,
            });
        };

        settle();
    }, []);

    return {
        latestFeedbacks,
        topQuestUsers,
        topTriggerUsers,
        userGrowth,
        latestReferralCodes,
        accountDeletions,
    };
};
